import React, { useState, useContext, useEffect, useMemo } from "react";
import './PolymatchReg.css'; // Make sure to link the CSS file
import { UserContext } from '../UserContext';
import StudentNav from '../Navbars/StudentNav';
import AdminNav from '../Navbars/AdminNav';
import GuestNav from '../Navbars/GuestNav';
import TeacherNav from '../Navbars/TeacherNav';
import Footer from "../Footer";
import { useNavigate } from 'react-router-dom/dist/umd/react-router-dom.development';
import Select from 'react-select'; // Import react-select
import countryList from 'react-select-country-list'; // Import react-select-country-list
import { FaEnvelope, FaExclamationCircle, FaUser, FaMapMarkerAlt, FaGraduationCap, FaCamera, FaPhone, FaBuilding } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import imageCompression from 'browser-image-compression'; // Import the image compression library

// Import images for the header section
import polymatchLogo from '../../assets/polymatchlogo.png';
import poly1 from '../../assets/login1.png';
import poly2 from '../../assets/login2.png';
// Import image for the mid-section
import polymatchImage from '../../assets/Polymatch.png';

const PolymatchReg = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    age: "",
    parentName: "",
    email: "",
    city: "",
    whatsappNumber: "",
    gender: null,
    location: "",
    country: null,
    school: "",
    childPhoto: null,
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null); // State for image preview
  const [competitionData, setCompetitionData] = useState([]); // Keep this for competition data if needed

  const { isAdmin, isTeacher, isStudent, isGuest, setUserRole, setUserInfo, userInfo } = useContext(UserContext);

  const username = userInfo?.username;
  const _avatar_ = userInfo?.avatar;

  const renderNavbar = () => {
    if (isAdmin) {
      return <AdminNav username={username} avatar={_avatar_} onLogout={onLogout} />;
    } else if (isTeacher) {
      return <TeacherNav username={username} avatar={_avatar_} onLogout={onLogout} />;
    } else if (isStudent) {
      return <StudentNav username={username} avatar={_avatar_} onLogout={onLogout} />;
    } else {
      return <GuestNav />;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}`, {
        credentials: 'include',
      }).then((response) => {
        response.json().then((userInfo) => {
          setUserInfo(userInfo);
          if (userInfo?.usertype) {
            setUserRole(userInfo?.usertype);
          }
        });
      });
    };

    const fetchCompetitionData = async () => {
      await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/polymatchcompetition`, {
        credentials: 'include',
      }).then((response) => {
        response.json().then((CompetitionData) => {
          setCompetitionData(CompetitionData);
        });
      });
    };

    fetchData();
    fetchCompetitionData();
  }, [isAdmin, isTeacher, isStudent, isGuest]);

  const onLogout = () => {
    fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/logout`, {
      credentials: 'include',
      method: 'POST',
    });
    setUserInfo(null);
    setUserRole('guest');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({
      ...formData,
      [name]: selectedOption,
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes (Adjusted from 200MB to 2MB as per common practice)
      if (file.size > maxSize) {
        toast.error('Image size should not be more than 2MB.', {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      // Compress the image
      const options = {
        maxSizeMB: 1, // Maximum size in MB
        maxWidthOrHeight: 500, // Maximum width or height for avatar
        useWebWorker: true, // Use web worker for better performance
      };

      try {
        const compressedFile = await imageCompression(file, options);
        // Convert to File object with proper extension
        const finalFile = new File([compressedFile], `child_${Date.now()}_${formData.firstName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });

        setFormData({ ...formData, childPhoto: finalFile });
        setImagePreview(URL.createObjectURL(finalFile));
      } catch (error) {
        console.error('Error compressing the image:', error);
        toast.error('Error compressing the image.', {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!formData.age.trim()) newErrors.age = "Age of Child is required";
    else if (!/^\d+$/.test(formData.age)) newErrors.age = "Age must be a number";
    if (!formData.parentName.trim()) newErrors.parentName = "Parent Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.whatsappNumber.trim()) newErrors.whatsappNumber = "WhatsApp number is required";
    else if (!/^\+?[\d\s-]{10,}$/.test(formData.whatsappNumber)) {
      newErrors.whatsappNumber = "Please enter a valid WhatsApp number";
    }
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.location.trim()) newErrors.location = "Address is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.school.trim()) newErrors.school = "School is required";
    if (!formData.childPhoto) newErrors.childPhoto = "Child photo is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly!", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
  
    setLoading(true);
    try {
      const data = new FormData();
      for (const key in formData) {
        if (formData[key] !== null && formData[key] !== undefined) {
          if (key === "gender" || key === "country") {
            data.append(key, formData[key].value);
          } else {
            data.append(key, formData[key]);
          }
        }
      }
      // Append Competition_Id if available
      if (competitionData.Competition_Id) {
        data.append('Competition_Id', competitionData.Competition_Id);
      }

      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/polymatchregister`, {
        method: "POST",
        body: data,
      });

      if (response.ok) {
        toast.success("Registration successful! A member of our team will contact you shortly.", {
          position: "top-center",
          autoClose: 2000,
        });
        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          age: "",
          parentName: "",
          email: "",
          city: "",
          whatsappNumber: "",
          gender: null,
          location: "",
          country: null,
          school: "",
          childPhoto: null,
        });
        setImagePreview(null);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Registration failed!", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("An error occurred during registration. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const countryOptions = useMemo(() => countryList().getData(), []);

  return (
    <>
      {renderNavbar()}

      <section className="PolymatchReg-headerSection"> {/* Using a new class for this header */}
        <div className="PolymatchReg-headerContainer">
          <h1 className="PolymatchReg-newHeaderHeading"> {/* New heading */} 
            <span className="PolymatchReg-newHeaderHeadingPart1">Register for the Global Science</span><br />
            <span className="PolymatchReg-newHeaderHeadingPart2">Knowledge Competition 2025</span>
          </h1>
          <p className="PolymatchReg-headerParagraph"> {/* Changed to paragraph */}
            Please fill out the form below with your child's accurate full name, grade, and school details as these will appear on the certificate. Include parent or guardian contact information for communication purposes. Ensure all school/institution details are correct, and review everything before submission. By registering, you confirm that the information provided is true and you consent to participation in the Global Science Knowledge Competition 2025 on June 20, 2025.
          </p>
          {/* Removed buttons from here */}
          <img src={poly1} alt="Poly 1" className="PolymatchReg-poly1Img" />
          <img src={poly2} alt="Poly 2" className="PolymatchReg-poly2Img" />
        </div>
      </section>

      <section className="PolymatchReg-formSection"> {/* New section for the form */}
        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div>

        <div className="PolymatchReg-midImageContainer">
          <img src={polymatchImage} alt="Polymatch" className="PolymatchReg-midImage" />
        </div>

        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        
        <h2 className="PolymatchReg-formHeading">
          <span className="PolymatchReg-formHeadingPart1">Register for the Global Science </span><br />
          <span className="PolymatchReg-formHeadingPart2">Knowledge Competition 2025</span>
            </h2>

        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        <div className="emptySpace"></div>
        
        <div className="PolymatchReg-formContainer">
          <ToastContainer position="top-center" autoClose={3000} pauseOnFocusLoss={false} draggable pauseOnHover={false} />
          <form onSubmit={handleSubmit} className="PolymatchReg-signupForm">
            <div className="PolymatchReg-formGrid">
              {/* This will be the first column, containing Photo and the original right-column fields */}
              <div className="PolymatchReg-columnLeftColumn">
                {/* Child Photo Upload Field */}
                <div className="PolymatchReg-photoUploadContainer">
                  <div className="PolymatchReg-formGroupPhotoField">
                    <input
                      type="file"
                      id="childPhoto"
                      name="childPhoto"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }} // Hide the default file input
                    />
                    <label htmlFor="childPhoto" className="PolymatchReg-photoUploadLabel">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Child Preview" className="PolymatchReg-circularImagePreview" />
                      ) : (
                        <div className="PolymatchReg-uploadPlaceholder-icon">
                          <FaCamera className="PolymatchReg-uploadIcon" />
                        </div>
                      )}
                    </label>
                  </div>
                  <span className="PolymatchReg-uploadText">Upload Child's Picture</span>
                </div>
                {errors.childPhoto && (
                  <span className="PolymatchReg-errorMessage-photoField">
                    <FaExclamationCircle /> {errors.childPhoto}
                  </span>
                )}

                <div className="PolymatchReg-formGroupParentNameField">
                  <div className="PolymatchReg-inputWrapper">
            <input
              type="text"
                      id="parentName"
                      name="parentName"
                      placeholder="Parent Name"
                      value={formData.parentName}
                      onChange={handleChange}
                      className={errors.parentName ? `PolymatchReg-error` : ""}
                    />
                    <FaUser className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.parentName && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.parentName}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupLastNameField">
                  <div className="PolymatchReg-inputWrapper">
            <input
              type="text"
                      id="lastName"
              name="lastName"
                      placeholder="Last Name"
              value={formData.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? `PolymatchReg-error` : ""}
                    />
                    <FaUser className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.lastName && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.lastName}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupWhatsappNumberField">
                  <div className="PolymatchReg-inputWrapper">
                    <input
                      type="tel"
                      id="whatsappNumber"
                      name="whatsappNumber"
                      placeholder="WhatsApp Number"
                      value={formData.whatsappNumber}
                      onChange={handleChange}
                      className={errors.whatsappNumber ? `PolymatchReg-error` : ""}
                    />
                    <FaPhone className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.whatsappNumber && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.whatsappNumber}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupLocationField">
                  <div className="PolymatchReg-inputWrapper">
                    <input
                      type="text"
                      id="location"
                      name="location"
                      placeholder="Address"
                      value={formData.location}
                      onChange={handleChange}
                      className={errors.location ? `PolymatchReg-error` : ""}
                    />
                    <FaMapMarkerAlt className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.location && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.location}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupSchoolField">
                  <div className="PolymatchReg-inputWrapper">
                    <input
                      type="text"
                      id="school"
                      name="school"
                      placeholder="School"
                      value={formData.school}
                      onChange={handleChange}
                      className={errors.school ? `PolymatchReg-error` : ""}
                    />
                    <FaGraduationCap className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.school && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.school}
                    </span>
                  )}
                </div>
              </div>

              <div className="PolymatchReg-columnRightColumn">
                <div className="PolymatchReg-formGroupFirstNameField">
                  <div className="PolymatchReg-inputWrapper">
            <input
              type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? `PolymatchReg-error` : ""}
                    />
                    <FaUser className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.firstName && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.firstName}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupAgeField">
                  <div className="PolymatchReg-inputWrapper">
                    <input
                      type="number"
                      id="age"
                      name="age"
                      placeholder="Age of Child"
                      value={formData.age}
                      onChange={handleChange}
                      className={errors.age ? `PolymatchReg-error` : ""}
                    />
                  </div>
                  {errors.age && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.age}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupGenderField">
                  <div className="PolymatchReg-inputWrapper">
                    <Select
                      id="gender"
                      name="gender"
                      placeholder="Select Gender"
                      value={formData.gender}
                      onChange={(selectedOption) => handleSelectChange("gender", selectedOption)}
                      options={genderOptions}
                      classNamePrefix="PolymatchReg-react-select"
                      className={errors.gender ? `PolymatchReg-errorSelect` : ""}
                      styles={{
                        control: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          border: 'none !important',
                          boxShadow: 'none !important',
                          minHeight: '58px',
                          borderRadius: '18px',
                          padding: '0 17px',
                          '&:hover': {
                            borderColor: '#DCDCDC'
                          }
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          padding: '0 !important',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#888 !important',
                        }),
                        input: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                          padding: '0 !important',
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: 'none !important',
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: '#888 !important',
                        }),
                        menu: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          zIndex: '100',
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '10px 17px',
                          fontSize: '16px',
                          color: '#333',
                          backgroundColor: state.isSelected ? '#812470' : state.isFocused ? '#e0e0e0' : 'white',
                          '&:active': {
                            backgroundColor: '#812470',
                          },
                        }),
                      }}
                    />
                  </div>
                  {errors.gender && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.gender}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupEmailField">
                  <div className="PolymatchReg-inputWrapper">
            <input
              type="email"
                      id="email"
              name="email"
              placeholder="Email"
              value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? `PolymatchReg-error` : ""}
                    />
                    <FaEnvelope className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.email && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.email}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupCityField">
                  <div className="PolymatchReg-inputWrapper">
            <input
              type="text"
                      id="city"
              name="city"
              placeholder="City"
              value={formData.city}
                      onChange={handleChange}
                      className={errors.city ? `PolymatchReg-error` : ""}
                    />
                    <FaBuilding className="PolymatchReg-inputIcon" />
                  </div>
                  {errors.city && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.city}
                    </span>
                  )}
                </div>

                <div className="PolymatchReg-formGroupCountryField">
                  <div className="PolymatchReg-inputWrapper">
            <Select
                      id="country"
                      name="country"
                      placeholder="Choose your country"
                      value={formData.country}
                      onChange={(selectedOption) => handleSelectChange("country", selectedOption)}
                      options={countryOptions}
                      classNamePrefix="PolymatchReg-react-select"
                      className={errors.country ? `PolymatchReg-errorSelect` : ""}
                      isSearchable={true}
                      isClearable={true}
                      menuPlacement="auto"
                      styles={{
                        control: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          border: 'none !important',
                          boxShadow: 'none !important',
                          minHeight: '58px',
                          borderRadius: '18px',
                          padding: '0 17px',
                          '&:hover': {
                            borderColor: '#DCDCDC'
                          }
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          padding: '0 !important',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#888 !important',
                        }),
                        input: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                          padding: '0 !important',
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: 'none !important',
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: '#888 !important',
                        }),
                        menu: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          zIndex: '100',
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '10px 17px',
                          fontSize: '16px',
                          color: '#333',
                          backgroundColor: state.isSelected ? '#812470' : state.isFocused ? '#e0e0e0' : 'white',
                          '&:active': {
                            backgroundColor: '#812470',
                          },
                        }),
                      }}
                    />
                  </div>
                  {errors.country && (
                    <span className="PolymatchReg-errorMessage">
                      <FaExclamationCircle /> {errors.country}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <p className="PolymatchReg-form-note">
              <b>Note:</b> If Registration form doesn't load/work, please inform us on WhatsApp <b> (+92 3266699907) </b> <b> (+47 47650619) </b>, our representative will register your profile
            </p>
            <button type="submit" className="PolymatchReg-signupButton" disabled={loading}>
              {loading ? "Signing Up..." : "Sign Up"}
            </button>
          </form>
        </div>

      </section>

      <Footer />
    </>
  );
};

export default PolymatchReg;